// AGC: AdminLogin - Admin panel login page
// AGC: Only allows users with 'admin' role to access the admin panel

import React, { useState, useEffect } from 'react'
import { useAuth } from '../../hooks'

interface AdminLoginProps {
  onLoginSuccess: () => void
}

/**
 * AGC: AdminLogin - Admin-only authentication screen
 * AGC: Verifies that the logged-in user has admin role
 */
export const AdminLogin: React.FC<AdminLoginProps> = ({ onLoginSuccess }) => {
  const {
    user,
    isLoading,
    isAuthenticated,
    error,
    clearError,
    loginWithEmail,
    loginWithGoogle,
    logout,
    canAccessAdmin,
  } = useAuth()

  // AGC: Form state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  // AGC: Check if authenticated user is admin
  useEffect(() => {
    if (isAuthenticated && user?.profile) {
      if (canAccessAdmin) {
        // AGC: User is admin, allow access
        console.log('✅ AGC: Admin access granted for:', user.email)
        onLoginSuccess()
      } else {
        // AGC: User is not admin, show error and log out
        setLocalError('Acesso negado. Você não tem permissão de administrador.')
        console.log('❌ AGC: Admin access denied for:', user.email, 'Role:', user.profile.role)
      }
    }
  }, [isAuthenticated, user, canAccessAdmin, onLoginSuccess])

  // AGC: Handle email login
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError(null)
    
    if (!email || !password) {
      setLocalError('Preencha todos os campos')
      return
    }

    await loginWithEmail({ email, password })
  }

  // AGC: Handle logout when access denied
  const handleLogout = async () => {
    await logout()
    setLocalError(null)
  }

  // AGC: If user is logged in but not admin
  if (isAuthenticated && user && !canAccessAdmin) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
            {/* AGC: Access Denied Icon */}
            <div className="w-20 h-20 bg-red-500/20 rounded-full mx-auto mb-6 flex items-center justify-center">
              <span className="text-4xl">🚫</span>
            </div>

            <h1 className="text-2xl font-bold text-white mb-4">
              Acesso Negado
            </h1>

            <p className="text-gray-400 mb-6">
              Você está logado como <strong className="text-white">{user.email}</strong>, mas não possui permissões de administrador.
            </p>

            <div className="bg-gray-700/50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-400">
                Seu nível de acesso: <span className="text-yellow-400 font-medium">{user.profile?.role || 'user'}</span>
              </p>
              <p className="text-sm text-gray-400 mt-1">
                Nível necessário: <span className="text-green-400 font-medium">admin</span>
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleLogout}
                className="w-full py-3 px-4 bg-gray-700 text-white rounded-xl font-medium hover:bg-gray-600 transition-colors"
              >
                Sair e tentar outra conta
              </button>

              <a
                href="/"
                className="block w-full py-3 px-4 border border-gray-600 text-gray-300 rounded-xl font-medium hover:bg-gray-700 transition-colors"
              >
                ← Voltar ao App Principal
              </a>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* AGC: Logo and Title */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gray-800 rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg border border-gray-700">
            <span className="text-4xl">🔐</span>
          </div>
          <h1 className="text-3xl font-bold text-white">Admin Panel</h1>
          <p className="text-gray-400 mt-2">DroidFarm - Área Administrativa</p>
        </div>

        {/* AGC: Login Card */}
        <div className="bg-gray-800 rounded-2xl shadow-xl p-6 border border-gray-700">
          {/* AGC: Warning banner */}
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-4 py-3 mb-6 flex items-start gap-3">
            <span className="text-xl">⚠️</span>
            <div>
              <p className="text-yellow-200 font-medium">Área Restrita</p>
              <p className="text-yellow-200/70 text-sm">
                Apenas administradores podem acessar este painel.
              </p>
            </div>
          </div>

          {/* AGC: Error message */}
          {(error || localError) && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg mb-4 flex justify-between items-center">
              <span>{localError || error}</span>
              <button 
                onClick={() => {
                  clearError()
                  setLocalError(null)
                }} 
                className="text-red-400 hover:text-red-300"
              >
                ✕
              </button>
            </div>
          )}

          {/* AGC: Login form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Email do Administrador
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu-email@exemplo.com"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Senha
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Verificando...' : 'Entrar como Admin'}
            </button>
          </form>

          {/* AGC: Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-800 text-gray-500">ou</span>
            </div>
          </div>

          {/* AGC: Google login */}
          <button
            onClick={loginWithGoogle}
            disabled={isLoading}
            className="w-full py-3 px-4 bg-gray-700 border border-gray-600 rounded-xl font-medium text-gray-300 hover:bg-gray-600 transition-colors flex items-center justify-center gap-3 disabled:opacity-50"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Entrar com Google
          </button>

          {/* AGC: Back to app */}
          <div className="mt-6 text-center">
            <a
              href="/"
              className="text-gray-400 hover:text-gray-300 text-sm"
            >
              ← Voltar ao App Principal
            </a>
          </div>
        </div>

        {/* AGC: Security notice */}
        <div className="mt-6 text-center">
          <p className="text-gray-500 text-xs">
            🔒 Conexão segura via Firebase Authentication
          </p>
        </div>
      </div>
    </div>
  )
}

export default AdminLogin
